<template>
  <BContainer
    v-if="currentUser"
    class="mb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <BSidebar
      id="sbRsmOfficerEventFilters"
      backdrop-variant="dark"
      backdrop
      shadow
      right
    >
      <template #title>
        <i class="fad fa-filter-list" /> Filters
      </template>
      <BContainer>
        <BRow>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Month"
              label-for="filterMonth"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <flat-pickr
                id="filterMonth"
                v-model="gridEvents.filter.month"
                class="form-control"
                placeholder="--------- ----"
                :config="flatPickerConfig"
              />
            </BFormGroup>
          </BCol>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Event Date"
              label-for="filterEventDate"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <BInputGroup>
                <BFormInput
                  id="filterEventDate"
                  v-model="gridEvents.filter.event_date"
                  debounce="1000"
                  type="date"
                  :min="gridEvents.minMonth"
                  :max="gridEvents.maxMonth"
                />
              </BInputGroup>
            </BFormGroup>
          </BCol>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Cluster"
              label-for="filterCluster"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="gridEvents.selected.cluster"
                placeholder="filter cluster here"
                input-id="filterCluster"
                label="cluster_name"
                class="border border-light rounded-lg"
                clearable
                append-to-body
                :options="(gridEvents.options.clusters)"
                :loading="(gridEvents.fetching.clusters)"
                :disabled="(gridEvents.busy || gridEvents.fetching.clusters)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterClusters"
              >
                <template #no-options="">
                  no available cluster
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Officer"
              label-for="filterOfficer"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="gridEvents.selected.fsOfficer"
                placeholder="filter officer here"
                input-id="filterOfficer"
                label="officer_name"
                clearable
                append-to-body
                :options="(gridEvents.options.fsOfficers)"
                :loading="(gridEvents.fetching.fsOfficers)"
                :disabled="(gridEvents.busy || gridEvents.fetching.fsOfficers)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterFSOfficers"
              >
                <template #option="{ officer_name, mobile_number, photo_url }">
                  <div class="d-flex flex-column flex-md-row py-1">
                    <div class="flex-grow-1 d-flex align-items-center">
                      <div class="mr-2">
                        <BAvatar
                          v-if="photo_url"
                          :src="getOfficerProfile(photo_url)"
                          variant="primary"
                          size="28px"
                        />
                        <BAvatar
                          v-else
                          :text="String(officer_name).charAt(0)"
                          variant="primary"
                          size="28px"
                        />
                      </div>
                      <div class="d-flex flex-column">
                        <div
                          class="text-break text-wrap"
                          v-text="officer_name"
                        />
                        <strong>{{ mobile_number || '' }}</strong>
                      </div>
                    </div>
                  </div>
                </template>
                <template #selected-option="{ officer_name, photo_url }">
                  <div class="d-flex flex-column flex-md-row">
                    <div class="flex-grow-1 d-flex align-items-center">
                      <div class="mr-2">
                        <BAvatar
                          v-if="photo_url"
                          :src="getOfficerProfile(photo_url)"
                          variant="primary"
                          size="28px"
                        />
                        <BAvatar
                          v-else
                          :text="String(officer_name).charAt(0)"
                          variant="primary"
                          size="28px"
                        />
                      </div>
                      <div
                        class="text-break text-wrap"
                        v-text="officer_name"
                      />
                    </div>
                  </div>
                </template>
                <template #no-options="">
                  no available officer
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>
          <BCol
            cols="12"
          >
            <BFormGroup
              label="Activity"
              label-for="filterActivity"
              label-class="font-weight-bolder"
              class="mb-2"
            >
              <VSelect
                v-model="gridEvents.selected.activity"
                placeholder="filter activity here"
                input-id="filterActivity"
                label="activity_name"
                class="border border-light rounded-lg"
                clearable
                append-to-body
                :options="(gridEvents.options.activities)"
                :loading="(gridEvents.fetching.activities)"
                :disabled="(gridEvents.busy || gridEvents.fetching.activities)"
                :calculate-position="calculateDropPosition"
                @search="searchFilterActivities"
              >
                <template #no-options="">
                  no available activity
                </template>
              </VSelect>
            </BFormGroup>
          </BCol>
        </BRow>
      </BContainer>
    </BSidebar>

    <BRow
      class="py-2 mb-3"
    >
      <BCol
        cols="12"
      >
        <BFormGroup
          label="Search"
          label-for="filterSearch"
          label-class="font-weight-bolder"
          class="mb-2"
        >
          <BInputGroup>
            <BFormInput
              id="filterSearch"
              v-model="gridEvents.filter.search"
              placeholder="search here"
              autocomplete="off"
              debounce="1000"
              type="text"
            />
            <BInputGroupAppend>
              <BButton
                v-b-toggle.sbRsmOfficerEventFilters
                variant="primary"
              >
                <i
                  v-if="[settingFilters, gridEvents.fetching.clusters, gridEvents.fetching.fsOfficers, gridEvents.fetching.activities].some(trueOrFalse => trueOrFalse)"
                  class="fad fa-spinner fa-spin p-0"
                />
                <i
                  v-else
                  class="fad fa-filter-list"
                /> Filters
              </BButton>
            </BInputGroupAppend>
          </BInputGroup>
        </BFormGroup>
      </BCol>
    </BRow>

    <BRow
      v-if="gridEvents.busy"
      class="gap-y-6 mb-3"
    >
      <BCol
        cols="12"
      >
        <div class="text-center py-5">
          <BIcon
            icon="stopwatch"
            font-scale="5"
            animation="cylon"
          />
          <p class="h3 py-2">
            <strong>Loading . . .</strong>
          </p>
        </div>
      </BCol>
    </BRow>

    <BRow
      v-else-if="Number(gridEvents.rows.length) <= 0 && !gridEvents.busy"
    >
      <BCol
        cols="12"
        class="text-center py-5"
      >
        <BIcon
          icon="calendar"
          font-scale="5"
        />
        <p class="h3 py-2">
          <strong>No events found</strong>
        </p>
      </BCol>
    </BRow>

    <BRow
      v-else
      class="gap-y-6 mb-3"
    >
      <BCol
        v-for="(event, index) in gridEvents.rows"
        :key="index"
        cols="12"
        lg="6"
      >
        <Event
          :details="{
            id: event.id,
            activity_name: event?.task?.activity?.activity_name || '',
            province_name: event?.province?.province_name || '',
            municipality_name: event?.municipality?.municipality_name || '',
            barangay_name: event?.barangay?.barangay_name || '',
            customer_name: event?.customer ? `${event.customer?.first_name || ''} ${event.customer?.last_name || ''}` : '',
            remarks: event.remarks,
            event_date: event.event_date
          }"
          :officer="{
            id: event?.task?.assignee?.id || '',
            photo_url: event?.task?.assignee?.photo_url || '',
            first_name: event?.task?.assignee?.first_name || '',
            last_name: event?.task?.assignee?.last_name || '',
          }"
          :attachment-path="event.attachment_path || ''"
          :attachments="event.attachments || []"
          :loading="gridEvents.busy"
          @show:attachment="attachmentIndex => openPictures(event, attachmentIndex)"
          @more:attachments="() => viewEvent(event, true)"
        >
          <template #footer>
            <div class="d-flex align-items-center justify-content-between mt-2">
              <div>
                {{ dateTimeShortFormatter(event.created_at) }}
              </div>
              <div class="d-flex gap-2">
                <BButton
                  size="sm"
                  variant="warning"
                  @click.prevent="viewEvent(event)"
                >
                  View
                </BButton>
              </div>
            </div>
          </template>
        </Event>
      </BCol>
    </BRow>

    <BRow>
      <BCol
        cols="12"
        md="6"
        class="mt-1 mb-md-0 mb-2"
      >
        <BSelect
          v-model="gridEvents.perPage"
          :options="gridEvents.pageOptions"
          :disabled="gridEvents.busy"
          class="w-100 w-md-75px"
          size="sm"
        />
      </BCol>

      <BCol
        cols="12"
        md="6"
        class="d-flex justify-content-center justify-content-md-end mt-1"
      >
        <BPagination
          v-if="gridEvents.currentPage"
          v-model="gridEvents.currentPage"
          :total-rows="gridEvents.totalRows"
          :per-page="gridEvents.perPage"
          :disabled="gridEvents.busy"
          class="mb-0"
          aria-controls="table"
          prev-text="Prev"
          next-text="Next"
          first-number
          last-number
          pills
        />
      </BCol>
    </BRow>

    <BModal
      ref="modalCreateEvent"
      size="lg"
      title="Task"
      scrollable
      centered
      hide-header
      no-close-on-esc
      no-enforce-focus
      no-close-on-backdrop
      content-class="scrollable-tabs"
      modal-class="full-height modal-no-padding"
      aria-hidden="false"
      role="dialog"
      @shown="eventModalShown"
      @hidden="eventModalHidden"
    >
      <BTabs
        v-model="state.modalEventTab"
        active-nav-item-class="font-weight-bold text-primary"
        nav-class="scrollable-x-tabs no-scrollbar nav-event-tabs gap-x-1"
        content-class="scrollable"
        pills
      >
        <template #tabs-start>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-left bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <template #tabs-end>
          <div class="nav-item">
            <div
              class="d-flex align-items-center position-absolute"
              style="right: 20px; padding: 2px 1px; height: 38px;"
            >
              <i
                class="fas fa-chevron-right bg-primary-light p-1 rounded-lg"
                style="font-size: 20px;"
              />
            </div>
          </div>
        </template>
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-file-lines" />
              <b class="is-tab-active ml-1">
                Details
              </b>
            </div>
          </template>

          <ValidationObserver
            ref="formEvent"
          >
            <form
              role="form"
              novalidate
              @submit.prevent
            >
              <BRow>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="activity_id"
                    name="activity"
                    rules="required"
                  >
                    <BFormGroup
                      label="Activity"
                      label-for="fActivityName"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fActivityName"
                        v-model="formEvent.activity_name"
                        type="text"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="event_date"
                    name="date"
                    rules="required"
                  >
                    <BFormGroup
                      label="Event Date"
                      label-for="fEventDate"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fEventDate"
                        v-model="formEvent.event_date"
                        type="date"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="customer_id"
                    name="customer"
                    rules="required"
                  >
                    <BFormGroup
                      label="Customer"
                      label-for="fCustomer"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fCustomer"
                        v-model="formEvent.customer_name"
                        type="text"
                        placeholder="no selected customer"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="province_id"
                    name="province"
                    rules="required"
                  >
                    <BFormGroup
                      label="Province"
                      label-for="fProvince"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fProvince"
                        v-model="formEvent.province_name"
                        type="text"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="municipality_id"
                    name="municipaliy"
                    rules="required"
                  >
                    <BFormGroup
                      label="Municipality"
                      label-for="fMunicipality"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <BFormInput
                        id="fMunicipality"
                        v-model="formEvent.municipality_name"
                        type="text"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol cols="12">
                  <ValidationProvider
                    v-slot="{ errors }"
                    vid="remarks"
                    name="remarks"
                    rules="required|max:1000"
                  >
                    <BFormGroup
                      label="Remarks"
                      label-for="fRemarks"
                      label-class="font-weight-bolder"
                      class="mb-2"
                    >
                      <template #label>
                        <span class="d-flex justify-content-between">
                          Remarks
                          <span>
                            {{ formEvent.remarks ? formEvent.remarks.length : 0 }} / 1000
                          </span>
                        </span>
                      </template>
                      <BFormTextarea
                        id="fRemarks"
                        v-model="formEvent.remarks"
                        placeholder="enter remarks here"
                        rows="7"
                        max-rows="9999"
                        disabled
                      />
                      <div
                        v-if="(errors.length > 0)"
                        class="invalid-feedback"
                        v-text="errors[0]"
                      />
                    </BFormGroup>
                  </ValidationProvider>
                </BCol>

                <BCol
                  cols="12"
                  md="6"
                >
                  <BFormGroup
                    label="Created By"
                    label-for="fCreatedBy"
                    label-class="font-weight-bolder"
                    class="mb-2"
                  >
                    <BFormInput
                      id="fCreatedBy"
                      v-model="formEvent.created_by"
                      type="text"
                      disabled
                    />
                  </BFormGroup>
                </BCol>
              </BRow>
            </form>
          </ValidationObserver>
        </BTab>
        <!-- <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-file-invoice-dollar" />
              <b class="is-tab-active ml-1">
                Sold Products
              </b>
            </div>
          </template>

          <Products
            ref="tableProducts"
            :products="formEvent.products"
            :use-form="!state.editing"
          />
        </BTab> -->
        <BTab
          class="p-2 min-h-400px"
        >
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-paperclip" />
              <b class="is-tab-active ml-1">
                Attachments
              </b>
            </div>
          </template>
          <Attachment
            ref="attachmentUploader"
            :path="formEvent.attachment_path"
            :files="formEvent.attachments"
            :loading="state.busy"
            :show-uploader="!state.editing"
          />
        </BTab>
        <BTab>
          <template #title>
            <div class="text-md-nowrap">
              <i class="fa-xl fad fa-map-marker-alt" />
              <b class="is-tab-active ml-1">
                Location
              </b>
            </div>
          </template>
          <GeoLocationMap
            ref="geoLocationMap"
            :enabled="false"
            :coords="formEvent.geolocation"
          />
        </BTab>
      </BTabs>
      <template #modal-footer="{ cancel }">
        <div class="w-100 d-flex justify-content-end align-items-center gap-2">
          <BButton

            variant="outline-dark"
            :disabled="state.busy"
            @click.prevent="discardBtnEvent => preventAriaHidden(discardBtnEvent, cancel)"
          >
            {{ state.editing ? 'Close' : 'Discard' }}
          </BButton>
        </div>
      </template>
    </BModal>

    <Preview
      ref="modalPreview"
    />
  </BContainer>
</template>
<script>
import QUERY from 'lodash'
import MOMENT from 'moment'
import FPMonthSelect from 'flatpickr/dist/plugins/monthSelect'
import { mapGetters } from 'vuex'
import { setTableOptions, getTableOptions } from '@/forage/asm/officers/events'

import Event from '@/views/asm/officers/events/components/Event.vue'
import Attachment from '@/views/asm/officers/task-events/components/Attachment.vue'
import Preview from '@/views/asm/officers/other-events/components/Preview.vue'
import GeoLocationMap from '@/components/custom/GeoLocationMap.vue'

import MxMisc from '@/mixins/misc'
import MxFormatter from '@/mixins/formatter'
import MxImageUrl from '@/mixins/image-url'
import MClusters from '@/mixins/locations/clusters'
import MCrops from '@/mixins/maintenances/crops'
import MActivities from '@/mixins/maintenances/activities'
import MFSOfficers from '@/mixins/accounts/fsos'
import MCustomers from '@/mixins/accounts/customers'

import { SASMFsoTaskEvent } from '@/services/asm'

export default {
  name: 'ASMOfficerEvents',

  components: {
    Event,
    Attachment,
    Preview,
    GeoLocationMap
  },

  mixins: [
    MxMisc,
    MxFormatter,
    MxImageUrl,
    MClusters,
    MCrops,
    MActivities,
    MFSOfficers,
    MCustomers
  ],

  beforeRouteEnter (_to, _from, navigate) {
    getTableOptions().then(options => {
      navigate(context => {
        if (context.currentUser) {
          context.state.settings = true

          context.gridEvents.selected.team = {
            id: context.currentUser?.team || ''
          }

          context.gridEvents.selected.area = {
            id: context.currentUser?.area || ''
          }

          if (options) {
            const { currentPage, perPage, sortBy, sortDesc, totalRows, filter } = options

            context.gridEvents = Object.assign(context.gridEvents, {
              totalRows: totalRows || 0,
              sortBy: sortBy || 'id',
              sortDesc: sortDesc || false,
              perPage: perPage || 10,
              currentPage: currentPage || 0,
              filter: {
                search: filter?.search || '',
                month: filter?.month || '',
                cluster: '',
                officer: '',
                activity: '',
                event_date: filter?.event_date || ''
              },
              editable: {
                cluster: filter?.cluster || '',
                fsOfficer: filter?.officer || '',
                activity: filter?.activity || ''
              }
            })
          } else {
            context.gridEvents.currentPage = 1
          }

          context.$nextTick(() => {
            context.getClusters(context.gridEvents, '', context.currentUser.area).finally(() => {
              if (context.gridEvents.editable.cluster) {
                context.gridEvents.selected.cluster = QUERY.find(context.gridEvents.options.clusters, {
                  id: context.gridEvents.editable.cluster
                }) || ''
              }

              context.getFSOfficers(
                context.gridEvents,
                '',
                context.currentUser.team,
                context.currentUser.area,
                context.gridEvents.editable.cluster
              ).finally(() => {
                if (context.gridEvents.editable.fsOfficer) {
                  context.gridEvents.selected.fsOfficer = QUERY.find(context.gridEvents.options.fsOfficers, {
                    id: context.gridEvents.editable.fsOfficer
                  }) || ''

                  context.gridEvents.editable.fsOfficer = ''
                }

                context.gridEvents.editable.cluster = ''

                context.getActivities(context.gridEvents).finally(() => {
                  if (context.gridEvents.editable.activity) {
                    context.gridEvents.selected.activity = QUERY.find(context.gridEvents.options.activities, {
                      id: context.gridEvents.editable.activity
                    }) || ''

                    context.gridEvents.editable.activity = ''
                  }

                  context.state.settings = false
                  context.gridProvider()
                })
              })
            })
          })
        }
      })
    })
  },

  middleware: [
    'auth',
    'asm'
  ],

  metaInfo: () => ({
    title: 'Officer Events'
  }),

  data () {
    return {
      flatPickerConfig: {
        appendTo: document.body,
        altInput: true,
        allowInput: true,
        disableMobile: true,
        plugins: [
          new FPMonthSelect({
            dateFormat: 'Y-m',
            altFormat: 'F Y'
          })
        ]
      },
      state: {
        busy: false,
        editing: false,
        settings: false,
        modalEventTab: 0
      },
      formEventValidation: {
        eventDate: {
          min: MOMENT().startOf('month').format('YYYY-MM-DD'),
          max: MOMENT().endOf('month').format('YYYY-MM-DD')
        }
      },
      selected: {
        role: 2
      },
      formEvent: {
        id: '',
        activity_id: '',
        activity_name: '',
        task_id: '',
        event_date: '',
        customer_id: '',
        customer_name: '',
        province_id: '',
        province_name: '',
        municipality_id: '',
        municipality_name: '',
        remarks: '',
        attachment_path: '',
        attachments: [],
        products: [],
        created_by: '',
        geolocation: {
          longitude: 0,
          latitude: 0
        },
        active: 1
      },
      gridEvents: {
        busy: false,
        fetching: {
          clusters: false,
          fsOfficers: false,
          activities: false
        },
        options: {
          clusters: [],
          fsOfficers: [],
          activities: []
        },
        selected: {
          role: 2,
          team: {
            id: ''
          },
          area: {
            id: ''
          },
          cluster: '',
          fsOfficer: '',
          activityType: 'Event',
          activity: ''
        },
        editable: {
          cluster: '',
          fsOfficer: '',
          activity: ''
        },
        minMonth: MOMENT().startOf('month').format('YYYY-MM-DD'),
        maxMonth: MOMENT().endOf('month').format('YYYY-MM-DD'),
        filter: {
          search: '',
          cluster: '',
          officer: '',
          activity: '',
          month: MOMENT().format('YYYY-MM'),
          event_date: MOMENT().format('YYYY-MM-DD')
        },
        filterOn: [],
        sortBy: 'id',
        sortDesc: false,
        sortDirection: 'asc',
        pageOptions: [10, 25, 50, 100],
        currentPage: 1,
        totalRows: 0,
        perPage: 10,
        rows: []
      }
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'auth/user'
    }),

    settingFilters () {
      const hasEditables = Object.keys(this.gridEvents.editable).map(
        editableKey => this.gridEvents.editable[editableKey]
      ).some(val => String(val) !== '')

      return hasEditables
    }
  },

  watch: {
    'gridEvents.selected.cluster' (cluster) {
      this.gridEvents.filter.cluster = cluster?.id || ''
    },

    'gridEvents.selected.fsOfficer' (fsOfficer) {
      this.gridEvents.filter.officer = fsOfficer?.id || ''
    },

    'gridEvents.selected.activity' (activity) {
      this.gridEvents.filter.activity = activity?.id || ''
    },

    'gridEvents.filter': {
      handler (filters) {
        if (!this.state.settings) {
          if (filters.month) {
            this.gridEvents.minMonth = MOMENT(filters.month, 'YYYY-MM').startOf('month').format('YYYY-MM-DD')
            this.gridEvents.maxMonth = MOMENT(filters.month, 'YYYY-MM').endOf('month').format('YYYY-MM-DD')
          } else {
            this.gridEvents.minMonth = ''
            this.gridEvents.maxMonth = ''
          }

          this.gridProvider()
        }
      },
      deep: true
    },

    'gridEvents.filter.cluster' (cluster) {
      if (!this.state.settings) {
        this.getFSOfficers(this.gridEvents, '', this.currentUser.team, this.currentUser.area, cluster)
      }
    },

    'gridEvents.currentPage' () {
      if (!this.state.settings) {
        this.gridProvider()
      }
    },

    'gridEvents.perPage' () {
      if (!this.state.settings) {
        this.gridProvider()
      }
    }
  },

  methods: {

    async gridProvider () {
      this.gridEvents.busy = true

      const payload = {
        page: this.gridEvents.currentPage,
        per_page: this.gridEvents.perPage,
        sort: this.gridEvents.sortBy,
        sort_desc: this.gridEvents.sortDesc,
        filter_text: this.gridEvents.filter.search,
        assignee_id: this.gridEvents.filter.officer,
        area_id: this.currentUser.area,
        cluster_id: this.gridEvents.filter.cluster,
        activity_id: this.gridEvents.filter.activity,
        schedule: this.gridEvents.filter.month,
        event_date: this.gridEvents.filter.event_date
      }

      return SASMFsoTaskEvent.get(payload).then(
        ({ data }) => {
          this.gridEvents.totalRows = data.total_rows
          this.gridEvents.rows = data.items

          setTableOptions({
            currentPage: this.gridEvents.currentPage,
            perPage: this.gridEvents.perPage,
            sortBy: this.gridEvents.sortBy,
            sortDesc: this.gridEvents.sortDesc,
            totalRows: data.total_rows,
            filter: this.gridEvents.filter
          })

          return data.items
        }
      ).catch(() => {
        this.gridEvents.totalRows = 0
        this.gridEvents.rows = []

        setTableOptions({
          currentPage: this.gridEvents.currentPage,
          perPage: this.gridEvents.perPage,
          sortBy: this.gridEvents.sortBy,
          sortDesc: this.gridEvents.sortDesc,
          totalRows: 0,
          filter: this.gridEvents.filter
        })
        return []
      }).finally(() => {
        this.gridEvents.busy = false
      })
    },

    async searchFilterClusters (search, loading) {
      this.searchClustersProvider(this.gridEvents, search, loading)
    },

    async searchFilterFSOfficers (search, loading) {
      this.searchFSOfficersProvider(this.gridEvents, search, loading)
    },

    async searchFilterActivities (search, loading) {
      this.searchActivitiesProvider(this.gridEvents, search, loading)
    },

    openPictures (activity, index) {
      this.$refs.modalPreview.show(activity.attachments, activity.attachment_path, index)
    },

    viewEvent (event, goToAttachments = false) {
      this.state.editing = true

      this.formEvent = Object.assign(this.formEvent, {
        id: event.id,
        task_id: event.task_id,
        activity_id: event.task?.activity.activity_id || '',
        activity_name: event?.task.activity?.activity_name || '',
        event_date: event.event_date,
        customer_id: event.customer_id,
        customer_name: event?.customer ? `${event?.customer?.first_name} ${event?.customer?.last_name}` : '',
        province_id: event.province_id,
        province_name: event?.province?.province_name || '',
        municipality_id: event.municipality_id,
        municipality_name: event?.municipality?.municipality_name || '',
        remarks: event.remarks,
        attachment_path: event.attachment_path,
        attachments: event.attachments || [],
        products: event?.purchases || [],
        created_by: event?.task?.assignee ? `${event?.task?.assignee?.first_name} ${event?.task?.assignee?.last_name}` : '',
        geolocation: event?.geolocation || {
          longitude: 0,
          latitude: 0
        },
        active: event.active
      })

      if (goToAttachments) {
        this.state.modalEventTab = 1
      } else {
        this.state.modalEventTab = 0
      }

      this.$refs.modalCreateEvent.show()
    },

    eventModalHidden () {
      this.detachArrowScroller('.nav-event-tabs')
    },

    eventModalShown () {
      this.attachArrowScroller('.nav-event-tabs')

      this.$refs.geoLocationMap.initMap()
    }
  }
}
</script>
